import React from 'react';

import { globalObservability } from '@opendoor/observability/slim';
import { getCustomerNavigationHref } from 'components/account/impersonation';
import { z } from 'zod';

import type { AddressValidationResult, SellerLeadChannel } from '../__generated__/zeus/types';
import { startAddressVerification } from '../components/api/addressInputHelpers';
import type { AutoCompletePrediction, IAddressInputAddress } from '../components/api/addressTypes';
import { redactGraphqlRequestErrorForTelemetry } from '../components/api/graphqlRequestError';
import {
  confirmSellerAddressEntry,
  parseSellerLeadChannel,
  SellerAddressIntakeError,
} from '../components/api/sellerAddressIntake';
import type { SellerOwnerRelationshipAnswer } from '../components/api/sellerAddressIntake';
import { isSellerLeadCakeTreatment } from '../components/api/sellerLeadCakeRollout';
import { CONSUMER_FE_URL, COSMOS_URL } from '../components/globals';
import { setCookie } from '../components/shared/trackers';
import { withImageParams } from './imageUrl';
import type { useObservability } from './observability';
import useQueryParams, { getFirstQueryParam } from './queryParams';

const Sentry = globalObservability.getSentryClient();

const optionalQueryParam = (value: string | string[] | undefined): string | undefined => {
  const first = getFirstQueryParam(value)?.trim();
  return first || undefined;
};

export type SelectedAddress = IAddressInputAddress & {
  id?: string;
  result?: AddressValidationResult[];
  source?: 'google' | 'casa';
};

export const ADDRESS_INVALID_RESULT: AddressValidationResult[] = [
  'ADDRESS_NIL',
  'ADDRESS_UNKNOWN',
  'ADDRESS_MISSING_PREMISE',
  'ADDRESS_INVALID_PREMISE',
  'ADDRESS_NOT_SUPPORTED',
  'ADDRESS_NOT_REAL',
  'ADDRESS_INVALID',
  'ADDRESS_INVALID_STREET',
  'ADDRESS_INVALID_SUB_PREMISE',
  'ADDRESS_MISSING_BOX_NUMBER',
  'ADDRESS_INVALID_BOX_NUMBER',
  'ADDRESS_EXTRANEOUS_INPUT',
];

const addressValidationResultValues = {
  ADDRESS_COMING_SOON: 'ADDRESS_COMING_SOON',
  ADDRESS_EXTRANEOUS_INPUT: 'ADDRESS_EXTRANEOUS_INPUT',
  ADDRESS_INVALID: 'ADDRESS_INVALID',
  ADDRESS_INVALID_BOX_NUMBER: 'ADDRESS_INVALID_BOX_NUMBER',
  ADDRESS_INVALID_PREMISE: 'ADDRESS_INVALID_PREMISE',
  ADDRESS_INVALID_STREET: 'ADDRESS_INVALID_STREET',
  ADDRESS_INVALID_SUB_PREMISE: 'ADDRESS_INVALID_SUB_PREMISE',
  ADDRESS_MISSING_BOX_NUMBER: 'ADDRESS_MISSING_BOX_NUMBER',
  ADDRESS_MISSING_PREMISE: 'ADDRESS_MISSING_PREMISE',
  ADDRESS_MISSING_SUB_PREMISE: 'ADDRESS_MISSING_SUB_PREMISE',
  ADDRESS_NIL: 'ADDRESS_NIL',
  ADDRESS_NOT_REAL: 'ADDRESS_NOT_REAL',
  ADDRESS_NOT_SUPPORTED: 'ADDRESS_NOT_SUPPORTED',
  ADDRESS_UNKNOWN: 'ADDRESS_UNKNOWN',
  ADDRESS_VACANT: 'ADDRESS_VACANT',
  ADDRESS_VALID: 'ADDRESS_VALID',
  ADDRESS_VALID_STREET: 'ADDRESS_VALID_STREET',
} as const satisfies Record<AddressValidationResult, AddressValidationResult>;

const addressValidationQueryResultSchema = z.enum(addressValidationResultValues);

const parseAddressValidationResults = (value: unknown): AddressValidationResult[] => {
  if (value == null) return [];

  const values = Array.isArray(value) ? value : [value];
  return values.flatMap((result) => {
    const parsed = addressValidationQueryResultSchema.safeParse(result);
    return parsed.success ? [parsed.data] : [];
  });
};

const selectedAddressQuerySchema = z.object({
  city: z.string().default(''),
  id: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  postal_code: z.string().default(''),
  result: z.array(addressValidationQueryResultSchema).optional(),
  source: z.enum(['google', 'casa']).optional(),
  state: z.string().default(''),
  street1: z.string(),
  unit: z.string().default(''),
}) satisfies z.ZodType<SelectedAddress>;

export const trackAddressSuccess = (
  trackEvent: ReturnType<typeof useObservability>['trackEvent'],
  label: 'pdp-redirect' | 'onboarding-redirect',
) => {
  try {
    trackEvent('inputting_address_success', label);
  } catch (err) {
    Sentry.captureException?.(err);
  }
};

interface OnboardingFlowInput {
  address: SelectedAddress;
  trackEvent: ReturnType<typeof useObservability>['trackEvent'];
  trackingTaxonomy?: string;
  channel?: SellerLeadChannel;
  trackingKeywords?: string;
  inputLocation?: string;
  onSuccess?: (address: IAddressInputAddress, token: string, pdpUrl?: string) => void;
  partnerReferralId?: string;
  pageSetPath?: string;
  addressUUID?: string;
  canRedirect?: boolean;
  relationship?: SellerOwnerRelationshipAnswer;
  originSurface?: string;
  userConfirmed?: boolean;
}

const handleAddressVerificationSuccess = async ({
  address,
  token,
  trackEvent,
  pdpUrl,
  pageSetPath,
  onSuccess,
  relationship,
  originSurface,
}: {
  address: SelectedAddress;
  token: string;
  trackEvent: ReturnType<typeof useObservability>['trackEvent'];
  pdpUrl?: string;
  pageSetPath?: string;
  onSuccess?: (address: IAddressInputAddress, token: string, pdpUrl?: string) => void;
  relationship?: SellerOwnerRelationshipAnswer;
  originSurface?: string;
}): Promise<void> => {
  const appendOriginSurface = (href: string): string =>
    originSurface ? withImageParams(href, { originSurface }) : href;

  const performRedirection = async () => {
    if (pdpUrl) {
      trackAddressSuccess(trackEvent, 'pdp-redirect');
      if (onSuccess) {
        return onSuccess(address, token, pdpUrl);
      }
      window.location.href = getCustomerNavigationHref(
        appendOriginSurface(
          `${COSMOS_URL}${pdpUrl}?utm_source=seller&utm_medium=web&utm_campaign=ae_pdp`,
        ),
      );
    } else {
      if (onSuccess) {
        trackAddressSuccess(trackEvent, 'onboarding-redirect');
        return onSuccess(address, token, pdpUrl);
      }
      const treatment = await isSellerLeadCakeTreatment({ applicationId: token });
      trackAddressSuccess(trackEvent, 'onboarding-redirect');
      const questionnairePath = treatment ? '/dashboard/seller-lead' : '/seller-lead';
      const redirectUrl = pageSetPath
        ? `${CONSUMER_FE_URL}${questionnairePath}/${token}${pageSetPath}`
        : `${CONSUMER_FE_URL}${questionnairePath}/${token}`;
      window.location.href = getCustomerNavigationHref(appendOriginSurface(redirectUrl));
    }
  };
  try {
    await confirmSellerAddressEntry(token, relationship);
  } catch (error) {
    Sentry.captureException?.(redactGraphqlRequestErrorForTelemetry(error));
  }
  await performRedirection();
};

export const gateProceedToOnboardingFlow = async ({
  address,
  trackEvent,
  trackingTaxonomy,
  channel,
  trackingKeywords,
  inputLocation,
  onSuccess,
  partnerReferralId,
  pageSetPath,
  addressUUID,
  canRedirect = true,
  relationship,
  originSurface,
  userConfirmed,
}: OnboardingFlowInput) => {
  if (trackingTaxonomy) {
    setCookie('sourceForTracking', trackingTaxonomy, 1);
  }

  const result = await startAddressVerification({
    addressUUID,
    address,
    channel,
    trackingKeywords,
    inputLocation,
    partnerReferralId,
    userConfirmed,
  });

  if (result.status === 'success') {
    await handleAddressVerificationSuccess({
      address,
      token: result.applicationId,
      trackEvent,
      pdpUrl: result.pdpUrl,
      pageSetPath,
      onSuccess,
      relationship,
      originSurface,
    });
    return;
  }

  const { error } = result;
  if (error.kind === 'invalid-address' || error.kind === 'postal-code-mismatch') {
    if (canRedirect) {
      window.location.href = getCustomerNavigationHref('/manual-address-entry');
    } else {
      throw new SellerAddressIntakeError(error.kind, error.message);
    }
  } else {
    window.location.href = getCustomerNavigationHref('/error');
  }
};

type AddressValidationContextProps = {
  validatedAddress?: SelectedAddress;
  setValidatedAddress: React.Dispatch<React.SetStateAction<SelectedAddress | undefined>>;
  addressResults: AddressValidationResult[];
  setAddressResults: React.Dispatch<React.SetStateAction<AddressValidationResult[]>>;
  isPredictionSelectLoading: boolean;
  setIsPredictionSelectLoading: React.Dispatch<React.SetStateAction<boolean>>;
  firstPrediction: AutoCompletePrediction | null;
  setFirstPrediction: React.Dispatch<React.SetStateAction<AutoCompletePrediction | null>>;
  partnerReferralId?: string;
  setPartnerReferralId: React.Dispatch<React.SetStateAction<string | undefined>>;
  pageSetPath?: string;
  setPageSetPath: React.Dispatch<React.SetStateAction<string | undefined>>;
  trackingKeywords?: string;
  setTrackingKeywords: React.Dispatch<React.SetStateAction<string | undefined>>;
  channel?: SellerLeadChannel;
  setChannel: React.Dispatch<React.SetStateAction<SellerLeadChannel | undefined>>;
};

const AddressValidationContext = React.createContext<AddressValidationContextProps>({
  validatedAddress: undefined,
  setValidatedAddress: () => null,
  addressResults: [],
  setAddressResults: () => null,
  isPredictionSelectLoading: false,
  setIsPredictionSelectLoading: () => null,
  firstPrediction: null,
  setFirstPrediction: () => null,
  partnerReferralId: undefined,
  setPartnerReferralId: () => null,
  pageSetPath: undefined,
  setPageSetPath: () => null,
  trackingKeywords: undefined,
  setTrackingKeywords: () => null,
  channel: undefined,
  setChannel: () => null,
});

export const useAddressValidationContext = () => React.useContext(AddressValidationContext);

export const AddressValidationContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    address,
    result,
    street1: street1QueryParam,
    pageSetPath: pageSetPathQueryParam,
    trackingKeyword: trackingKeywordQueryParam,
    trackingKeywords: trackingKeywordsQueryParam,
    partnerReferralId: partnerReferralIdQueryParam,
    channel: channelQueryParam,
  } = useQueryParams();
  const [validatedAddress, setValidatedAddress] = React.useState<SelectedAddress | undefined>(
    () => {
      const serializedAddress = getFirstQueryParam(address);
      if (serializedAddress) {
        try {
          const parsed: unknown = JSON.parse(serializedAddress);
          const selectedAddress = selectedAddressQuerySchema.safeParse(parsed);
          if (selectedAddress.success) return selectedAddress.data;
          Sentry.captureException?.(new Error('Invalid address query parameter'));
        } catch {
          Sentry.captureException?.(new Error('Invalid address query parameter'));
        }
      }

      const street1 = optionalQueryParam(street1QueryParam);
      return street1 ? selectedAddressQuerySchema.parse({ street1 }) : undefined;
    },
  );

  const [addressResults, setAddressResults] = React.useState<AddressValidationResult[]>(() => {
    const queryResults = parseAddressValidationResults(result);
    return queryResults.length > 0 ? queryResults : (validatedAddress?.result ?? []);
  });
  const [isPredictionSelectLoading, setIsPredictionSelectLoading] = React.useState(false);
  const [firstPrediction, setFirstPrediction] = React.useState<AutoCompletePrediction | null>(null);
  const [partnerReferralId, setPartnerReferralId] = React.useState<string | undefined>(() => {
    return optionalQueryParam(partnerReferralIdQueryParam);
  });
  const [pageSetPath, setPageSetPath] = React.useState<string | undefined>(() => {
    return optionalQueryParam(pageSetPathQueryParam);
  });
  const [trackingKeywords, setTrackingKeywords] = React.useState<string | undefined>(() => {
    return (
      optionalQueryParam(trackingKeywordsQueryParam) ??
      optionalQueryParam(trackingKeywordQueryParam)
    );
  });
  const [channel, setChannel] = React.useState<SellerLeadChannel | undefined>(() => {
    return parseSellerLeadChannel(optionalQueryParam(channelQueryParam)) ?? 'DEFAULT';
  });
  return (
    <AddressValidationContext.Provider
      value={{
        trackingKeywords,
        setTrackingKeywords,
        pageSetPath,
        setPageSetPath,
        validatedAddress,
        setValidatedAddress,
        addressResults,
        setAddressResults,
        isPredictionSelectLoading,
        setIsPredictionSelectLoading,
        firstPrediction,
        setFirstPrediction,
        partnerReferralId,
        setPartnerReferralId,
        channel,
        setChannel,
      }}
    >
      {children}
    </AddressValidationContext.Provider>
  );
};
